import React, { useState, useEffect } from 'react'
import BoxHeaderNew, { DEFAULT_SLIPPAGE } from './BoxHeader/BoxHeader'
import { useStoreActions, useStoreState } from '../model/hooks'
import { isTransactionInProgress } from '../model/transaction/helpers'
import { useWalletContext } from './WalletContext'
import TokenSelectorNew from './TokenSelectorNew/TokenSelectorNew'
import { isSufficientBalanceInWallet } from '../model/tokens/helper'
import PrimaryButton from './PrimaryButton'
import { addLiquidity } from '../services/position'
import { Network } from '../services/token.service'
import { _getAmount0FromAmount1, _getAmount1FromAmount0 } from '../blockchainUtils/positionAmountsMath'
import { GAtrack } from '../services/ga'
import BigNumber from 'bignumber.js'
import { MAX_TICK } from './PoolsPage/helper'

export default function AddLiquidity(props: {
    positionReduxIndex: number
    poolAddress: string
    token1Amount: string
    token2Amount: string
    // balance: string
    token1Address: string
    token2Address: string
    currentRatio: string
    currentTick: number
    onBackClick: () => void
}) {
    const { token1Amount, token2Amount, token1Address, token2Address, positionReduxIndex, currentRatio, currentTick, onBackClick } = props
    const { walletAddress: connectedAddress, networkName: network } = useWalletContext()

    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const [txHash, setTxHash] = useState('')
    const transaction = useStoreState((state) => state.transaction.getTransaction(txHash))
    const token1 = useStoreState((state) => state.tokens.getToken(network!, token1Address))
    const token2 = useStoreState((state) => state.tokens.getToken(network!, token2Address))
    const loadBalances = useStoreActions((actions) => actions.tokens.loadBalances)
    const clPosition = useStoreState((state) => state.clPools.getCLPositionByIndex(network!, positionReduxIndex))
    const [token1AmountSelected, setToken1Amount] = useState(token1Amount && Number(token1Amount) > 0 ? token1Amount : '')
    const [token2AmountSelected, setToken2Amount] = useState(token2Amount && Number(token2Amount) > 0 ? token2Amount : '')
    const [leadingToken, setLeadingToken] = useState(1)
    const [slippage, setSlippage] = useState(Number(DEFAULT_SLIPPAGE))
    const [isAddingLiquidity, setIsAddingLiquidity] = useState(false)
    const loadPositons = useStoreActions((actions) => actions.clPools.loadPositons)
    const [tickOutOFRange, setTickOutOFRange] = useState('in-range' as 'in-range' | 'above-range' | 'below-range')

    useEffect(() => {
        if (transaction && (transaction.status === 'ACCEPTED_ON_L2' || transaction.status === 'ACCEPTED_ON_L1')) {
            setIsAddingLiquidity(false)
            loadBalances({ network: network as Network, walletAddress: connectedAddress! })
            loadPositons({ network: network as Network, address: connectedAddress!, selectedPosition: positionReduxIndex })
            onBackClick()
            setTxHash('')
        }
        if (transaction && transaction.status === 'REVERTED') {
            setIsAddingLiquidity(false)
            setTxHash('')
        }
    }, [transaction])

    useEffect(() => {
        if (!clPosition) return
        if (clPosition && clPosition.tickLower > currentTick + MAX_TICK) {
            setTickOutOFRange('below-range')
        }
        if (clPosition && clPosition.tickUpper < currentTick + +MAX_TICK) setTickOutOFRange('above-range')
    }, [clPosition, currentTick])

    useEffect(() => {
        if (!transaction || !transaction.status) return
        const isProgress = isTransactionInProgress(transaction.status)
        // setRemoveLiquidityInProgress(isProgress)
        if (!isProgress) setTxHash('')
    }, [transaction])

    useEffect(() => {
        console.log('-------CurrentRatio Updated Price is ' + currentRatio + ' -------')
        if (currentRatio && token1AmountSelected && token2AmountSelected && token1 && token2) {
            const tokenChanged = leadingToken === 1 ? token1?.address : token2?.address
            handleTokenAmountChange(tokenChanged!, leadingToken === 1 ? token1AmountSelected : token2AmountSelected)
        }
    }, [currentRatio])

    async function handleAddLiquidity() {
        if (!clPosition) return
        setIsAddingLiquidity(true)
        try {
            const response = await addLiquidity(
                network as Network,
                {
                    fee: clPosition!.fee!,
                    feeGrowthInside0LastX128: clPosition!.feeGrowthInside0LastX128!,
                    feeGrowthInside1LastX128: clPosition!.feeGrowthInside1LastX128!,
                    liquidity: clPosition?.liquidity,
                    positionIndex: clPosition?.positionIndex,
                    tickLower: clPosition?.tickLower,
                    tickUpper: clPosition?.tickUpper,
                    token0: clPosition?.token0,
                    token1: clPosition?.token1,
                    tokensOwed0: clPosition?.tokensOwed0,
                    tokensOwed1: clPosition?.tokensOwed1
                },
                clPosition.value,
                token1AmountSelected,
                token2AmountSelected,
                Number(slippage) / 100
            )

            if (network === 'mainnet-alpha') GAtrack('tx_initiated_add_liquidity', 'tx_initiated_add_liquidity', 'add_liquidity')

            // @ts-ignore
            const tx = response.tx
            if (tx) {
                const timestampSent = new Date().getTime()
                loadTransaction({ txPayload: { [tx.hash]: { ...tx, labelFields: { timestampSent: timestampSent } } }, isGoogleLog: network === 'mainnet-alpha' })
                setTxHash(tx.hash)
                setToken1Amount('')
                setToken2Amount('')
                const label = `add liquidity token1Address = ${token1} token2Address = ${token2}`
                if (network === 'mainnet-alpha') GAtrack('tx_sent_add_liquidity', 'tx_sent_add_liquidity', label)
                // GAtrack('NewTx', 'add_liquidity', tx.hash)
            } else {
                setIsAddingLiquidity(false)
            }
            // add to tx load
            // setIsAddingLiquidity(false)
        } catch (e: any) {
            console.log('ERROR ', e.message)
            setIsAddingLiquidity(false)
        }
        // }, 5000)
    }

    function getIsDisabled() {
        return Number(token1AmountSelected) === 0 && Number(token2AmountSelected) === 0
    }

    const handleTokenAmountChange = (token: string, amount: string) => {
        // fixme: consider limit decimals input according to token decimals
        const regex = /^(([0-9]+[.]?[0-9]*)|([.][0-9]+))$/

        console.log('---- last updated ' + new Date() + ' ----')
        if (token === token1?.address && (amount === '' || regex.test(amount))) {
            setToken1Amount(amount)
            setLeadingToken(1)
            if (Number(amount) === 0) return setToken2Amount('0')
            try {
                const token2amm = _getAmount1FromAmount0(
                    amount,
                    token1,
                    token2!,
                    clPosition!.tickLower - MAX_TICK,
                    clPosition!.tickUpper - MAX_TICK,
                    BigNumber(currentRatio).sqrt()
                )
                setToken2Amount(token2amm)
            } catch (err) {
                setToken2Amount('0')
            }
        }
        if (token === token2?.address && (amount === '' || regex.test(amount))) {
            setToken2Amount(amount)
            setLeadingToken(2)
            try {
                const token1amm = _getAmount0FromAmount1(
                    amount,
                    token1!,
                    token2,
                    clPosition!.tickLower - MAX_TICK,
                    clPosition!.tickUpper - MAX_TICK,
                    BigNumber(currentRatio).sqrt()
                )
                setToken1Amount(token1amm)
            } catch (err) {
                setToken1Amount('0')
            }
        }
    }

    function renderTokenSelector(
        token: string,
        tokenAmount: string,
        otherToken: string,
        handleTokenSelect: () => void,
        handleTokenAmountChange: (token: string, number: string) => void,
        tokenSelectName: string,
        className = '',
        isInputDisabled = false
    ) {
        return (
            <div
                className={`p-4 lg:px-4  ${className}`}
                style={{
                    background: isSufficientBalanceInWallet(network!, token, tokenAmount) ? '#1D285A' : 'rgba(255, 83, 83, 0.2)',
                    borderRadius: '16px',
                    boxShadow: '0px 3px 7px rgba(28, 28, 34, 0.33)'
                }}>
                <div className='flex justify-between'>
                    <div className='flex flex-1'>
                        <TokenSelectorNew
                            tokenAmount={tokenAmount}
                            error={isSufficientBalanceInWallet(network!, token, tokenAmount) ? '' : 'Exceeds Balance'}
                            network={network!}
                            token={token}
                            tokenStr={tokenSelectName}
                            otherToken={otherToken}
                            tokens={[]}
                            isSelectDisabled={true}
                            handleTokenSelect={handleTokenSelect}
                            showMaxButton={true}
                            handleTokenAmountChange={handleTokenAmountChange}
                            // inputs={null}
                            inputs={
                                <div className='flex justify-center mb-4 flex-col w-full text-right text-[18px] lg:text-[24px] font-bold items-end'>
                                    <input
                                        className={`bg-transparent text-right outline-none w-full lg:max-w-auto ${isInputDisabled ? 'cursor-not-allowed text-gray' : 'text-white'}`}
                                        disabled={isInputDisabled}
                                        placeholder='0.00'
                                        value={tokenAmount}
                                        onChange={(e) => handleTokenAmountChange(token, e.target.value)}
                                    />
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>
        )
    }

    function renderSwapHeader() {
        return <BoxHeaderNew slippage={slippage.toString()} setOutsideSlippage={(val) => setSlippage(val)} className='mb-4 lg:mb-6' name='Add Liquidity' />
    }

    function renderAddLiquidityScreen() {
        return (
            <div className='max-w-[528px] m-auto'>
                {renderSwapHeader()}
                {renderTokenSelector(
                    token1?.address!,
                    token1AmountSelected,
                    token2?.address!,
                    () => {},
                    handleTokenAmountChange,
                    'Token 1',
                    'mb-2',
                    tickOutOFRange === 'above-range'
                )}
                {renderTokenSelector(token2?.address!, token2AmountSelected, token1?.address!, () => {}, handleTokenAmountChange, 'Token 2', '', tickOutOFRange === 'below-range')}

                <PrimaryButton isLoading={isAddingLiquidity} onClick={handleAddLiquidity} isDisabled={getIsDisabled()} className='mt-4'>
                    <span>{isAddingLiquidity ? 'Adding Liquidity' : 'Add Liquidity'}</span>
                </PrimaryButton>
            </div>
        )
    }

    return renderAddLiquidityScreen()
}
